import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls =
  "w-full bg-[#0D0E13] border border-white/10 text-white text-sm px-4 py-3 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!mfaToken) {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) {
          setMfaToken(data.mfa_token);
          return;
        }
        localStorage.setItem("zt_token", data.token);
        toast.success("Access granted.");
        navigate("/admin");
      } else {
        const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
        localStorage.setItem("zt_token", data.token);
        toast.success("Access granted.");
        navigate("/admin");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="w-full max-w-sm border border-white/10 bg-[#151821] p-8">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo-dark.png" alt="ZTrust Africa" className="h-8 w-auto" />
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Admin</span>
        </div>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">
          Restricted — authorized personnel only
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          {!mfaToken ? (
            <>
              <input
                type="email"
                required
                data-testid="admin-email-input"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
              <input
                type="password"
                required
                data-testid="admin-password-input"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </>
          ) : (
            <>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">
                Step 2 — enter the 6-digit code from your authenticator app
              </p>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                data-testid="mfa-code-input"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${inputCls} text-center tracking-[0.5em] text-lg font-mono`}
              />
            </>
          )}
          {error && (
            <p data-testid="admin-login-error" className="text-destructive text-xs font-mono">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit-btn"
            className="w-full bg-primary text-background font-bold py-3.5 text-sm hover:bg-[#7AD6E6] disabled:opacity-50 transition-colors duration-300"
          >
            {busy ? "Authenticating..." : mfaToken ? "Verify Code" : "Authenticate"}
          </button>
        </form>
        <a href="/" data-testid="back-to-site-link" className="block mt-6 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors duration-300">
          ← Back to site
        </a>
      </div>
    </div>
  );
}
